/**
 * Catalogo de proveedores de pago, espejo de `PROVIDERS` en
 * `abogado_pago_backend/src/utils/constants.utils.js`.
 *
 * Se mantiene local (y no se pide a `GET /api/requests/providers`) porque la
 * seleccion ocurre en el paso 0 del onboarding, donde el usuario todavia puede no
 * tener token, y el catalogo solo aporta iconos y copys que son de UI.
 *
 * Si cambia el catalogo del backend, actualizar aqui.
 */

export const PROVIDER_CHOICE = {
  USIO: 'usio',
  DWOLLA: 'dwolla',
  BOTH: 'both',
} as const;

export type ProviderChoice = (typeof PROVIDER_CHOICE)[keyof typeof PROVIDER_CHOICE] | null;

export interface ProviderOption {
  code: 'usio' | 'dwolla';
  name: string;
  /** Metodos de cobro que habilita, para el copy de la tarjeta. */
  methods: string[];
  requiresDocuments: boolean;
  description: string;
}

export const PROVIDERS: ProviderOption[] = [
  {
    code: 'usio',
    name: 'Usio',
    methods: ['Cards', 'ACH'],
    requiresDocuments: false,
    description:
      'Card and ACH payments. Short application, no identity documents required.',
  },
  {
    code: 'dwolla',
    name: 'Dwolla',
    methods: ['Bank transfer'],
    requiresDocuments: true,
    description:
      'Bank-to-bank transfers. Requires beneficial owners and identity documents.',
  },
];

/**
 * Valor especial de la seleccion: el abogado no elige proveedor ahora y
 * configura su cuenta mas tarde desde su subdominio.
 *
 * Se representa como `null` en el contexto para que `provider_choice` viaje
 * vacio al backend y la solicitud quede marcada como `deferred`.
 */
export const DEFERRED_OPTION = 'deferred';

/** Origen de la solicitud, espejo de `REQUEST_ORIGIN` del backend. */
export const REQUEST_ORIGIN = {
  LANDING: 'landing',
  SUBDOMAIN: 'subdomain',
} as const;

/**
 * Lo que el abogado puede elegir en el paso de proveedor: uno de los proveedores
 * o "configurar despues". `null` es "todavia no eligio nada".
 */
export type ProviderSelection =
  | typeof PROVIDER_CHOICE.USIO
  | typeof PROVIDER_CHOICE.DWOLLA
  | typeof PROVIDER_CHOICE.BOTH
  | typeof DEFERRED_OPTION;

/** Clave de localStorage donde se recuerda la eleccion entre recargas. */
export const PROVIDER_STORAGE_KEY = 'enrollProviderChoice';

/**
 * Traduce la eleccion de la UI al valor que espera el backend en
 * `provider_choice`. La opcion "configurar despues" viaja como `null`.
 */
export const toBackendChoice = (
  selection: ProviderSelection | null,
): ProviderChoice => {
  if (!selection || selection === DEFERRED_OPTION) return null;
  return selection;
};

/**
 * Traduce la eleccion a los flags `is_usio` / `is_dwolla` que espera el backend.
 *
 * `null` (configurar despues) devuelve ambos en `false`, que es lo que hace que
 * la solicitud se cree sin informacion de proveedor.
 */
export const providerFlags = (choice: ProviderSelection | null) => ({
  is_usio: choice === PROVIDER_CHOICE.USIO || choice === PROVIDER_CHOICE.BOTH,
  is_dwolla: choice === PROVIDER_CHOICE.DWOLLA || choice === PROVIDER_CHOICE.BOTH,
});

/** Etiqueta legible para el resumen y el backoffice. */
export const providerLabel = (choice: ProviderSelection | null): string => {
  switch (choice) {
    case PROVIDER_CHOICE.USIO:
      return 'Usio';
    case PROVIDER_CHOICE.DWOLLA:
      return 'Dwolla';
    case PROVIDER_CHOICE.BOTH:
      return 'Usio + Dwolla';
    case DEFERRED_OPTION:
      return 'Configure later';
    default:
      return 'Configure later';
  }
};
